/**
 * 드론 경로 분석 모듈
 * 벡터 각도 분석 및 needFilm 체크 대상 판단
 */

import { PATH_ANALYSIS_CONFIG, AngleChecker } from './imageAnalysisConfig.js';
import { calculateVectorAngle } from './geometryUtils.js';

/**
 * 경로 분석 클래스
 */
export class PathAnalyzer {
  constructor() {
    this.config = PATH_ANALYSIS_CONFIG;
    this.angleChecker = AngleChecker;
  }

  /**
   * 디버깅용 파일인지 확인
   */
  isDebugFile(fileName) {
    return this.config.debugFiles.some(debugFile => fileName.includes(debugFile));
  }

  /**
   * 각도 정보를 포함한 결과 객체 생성
   */
  createAngleInfoResult(prevPoint, nextPoint, angleInfo) {
    return {
      prevPoint,
      nextPoint,
      angleInfo
    };
  }

  /**
   * 첫번째 벡터 처리
   */
  handleFirstVector(prevPoint, nextPoint, isTargetFile) {
    if (isTargetFile) {
      console.log(`  ✅ 첫번째 벡터로 판단`);
    }
    return this.createAngleInfoResult(prevPoint, nextPoint, {
      isFirstVector: true,
      beforeAngle: null,
      nextAngle: null,
      isDirectionChange: false
    });
  }

  /**
   * 두번째 벡터 처리
   */
  handleSecondVector(prevPoint, nextPoint, nextAngle, isTargetFile) {
    const smallAngle = this.config.angles.straight.small;
    
    if (isTargetFile) {
      console.log(`  🔍 두번째 벡터 조건 체크 - nextAngle:`, nextAngle);
    }
    
    if (!isNaN(nextAngle) && this.angleChecker.isSmallAngle(nextAngle)) {
      if (isTargetFile) {
        console.log(`  ✅ 두번째 벡터 - 직선 경로로 판단`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: true,
        beforeAngle: null,
        nextAngle: nextAngle,
        isDirectionChange: false
      });
    }
    
    if (isTargetFile) {
      console.log(`  ❌ 두번째 벡터 - ${smallAngle}도 이상 각도 변화로 제외`);
    }
    return null;
  }

  /**
   * 직선 경로 케이스 판단 (케이스 1)
   * 태양광 패널을 지나는 직선 경로를 포괄적으로 판단
   */
  checkStraightPathCases(beforeAngle, nextAngle, prevPoint, nextPoint, isTargetFile) {
    const absBefore = Math.abs(beforeAngle);
    const absNext = Math.abs(nextAngle);
    const small = this.config.angles.straight.small;
    const medium = this.config.angles.straight.medium;
    const large = this.config.angles.straight.large;

    // 케이스 1-1: beforeAngle과 nextAngle이 모두 작은 경우
    if (absBefore <= small && absNext <= small) {
      if (isTargetFile) {
        console.log(`  ✅ 케이스 1-1: 완전한 직선 경로 (beforeAngle: ${beforeAngle}도, nextAngle: ${nextAngle}도)`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: false
      });
    }

    // 케이스 1-2: beforeAngle이 작고 nextAngle이 중간 범위
    if (absBefore <= small && absNext > small && absNext <= medium) {
      if (isTargetFile) {
        console.log(`  ✅ 케이스 1-2: 직선 경로 연속 (beforeAngle: ${beforeAngle}도, nextAngle: ${nextAngle}도)`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: false
      });
    }

    // 케이스 1-3: beforeAngle이 중간 범위이고 nextAngle이 작은 경우
    if (absBefore > small && absBefore <= medium && absNext <= small) {
      if (isTargetFile) {
        console.log(`  ✅ 케이스 1-3: 직선 경로 (beforeAngle: ${beforeAngle}도, nextAngle: ${nextAngle}도)`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: false
      });
    }

    // 케이스 1-4: beforeAngle과 nextAngle이 모두 중간 범위
    if (absBefore > small && absBefore <= medium && absNext > small && absNext <= medium) {
      if (isTargetFile) {
        console.log(`  ✅ 케이스 1-4: 직선 경로 (beforeAngle: ${beforeAngle}도, nextAngle: ${nextAngle}도)`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: false
      });
    }

    // 케이스 1-5: beforeAngle이 작은 경우 (nextAngle이 크더라도)
    if (absBefore <= small) {
      if (isTargetFile) {
        console.log(`  ✅ 케이스 1-5: 직선 경로 연속 (beforeAngle: ${beforeAngle}도, nextAngle: ${nextAngle}도)`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: false
      });
    }

    // 케이스 1-6: beforeAngle이 중간 범위이고 nextAngle이 큰 범위 (60~90도)
    if (absBefore > small && absBefore <= medium && absNext > medium && absNext <= large) {
      if (isTargetFile) {
        console.log(`  ✅ 케이스 1-6: 직선 경로 (태양광 패널 구간) (beforeAngle: ${beforeAngle}도, nextAngle: ${nextAngle}도)`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: false
      });
    }

    // 케이스 1-7: beforeAngle이 큰 범위(60~90도)이고 nextAngle이 작거나 중간 범위
    if (absBefore > medium && absBefore <= large && absNext <= medium) {
      if (isTargetFile) {
        console.log(`  ✅ 케이스 1-7: 직선 경로 (태양광 패널 구간) (beforeAngle: ${beforeAngle}도, nextAngle: ${nextAngle}도)`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: false
      });
    }

    // 케이스 1-8: beforeAngle과 nextAngle이 모두 큰 범위(60~90도)이지만 90도 회전은 아님
    if (absBefore > medium && absBefore <= large && absNext > medium && absNext <= large) {
      if (isTargetFile) {
        console.log(`  ✅ 케이스 1-8: 직선 경로 (태양광 패널 구간) (beforeAngle: ${beforeAngle}도, nextAngle: ${nextAngle}도)`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: false
      });
    }

    // 케이스 1-9: beforeAngle이 중간 범위이고 nextAngle이 90도보다 크지만 120도 미만
    if (absBefore > small && absBefore <= medium && absNext > large && absNext < 120) {
      if (isTargetFile) {
        console.log(`  ✅ 케이스 1-9: 직선 경로 (태양광 패널 구간) (beforeAngle: ${beforeAngle}도, nextAngle: ${nextAngle}도)`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: false
      });
    }

    // 케이스 1-10: beforeAngle이 90도보다 크지만 120도 미만이고 nextAngle이 작거나 중간 범위
    if (absBefore > large && absBefore < 120 && absNext <= medium) {
      if (isTargetFile) {
        console.log(`  ✅ 케이스 1-10: 직선 경로 (태양광 패널 구간) (beforeAngle: ${beforeAngle}도, nextAngle: ${nextAngle}도)`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: false
      });
    }

    // 케이스 1-11: beforeAngle과 nextAngle이 모두 90도보다 크지만 120도 미만
    if (absBefore > large && absBefore < 120 && absNext > large && absNext < 120) {
      if (isTargetFile) {
        console.log(`  ✅ 케이스 1-11: 직선 경로 (태양광 패널 구간) (beforeAngle: ${beforeAngle}도, nextAngle: ${nextAngle}도)`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: false
      });
    }

    return null;
  }

  /**
   * 90도 회전 케이스 판단 (케이스 2)
   */
  check90DegreeTurnCases(beforeAngle, nextAngle, prevPoint, nextPoint, isTargetFile) {
    const absNext = Math.abs(nextAngle);
    const small = this.config.angles.straight.small;

    if (isTargetFile) {
      console.log(`  🔍 케이스 2: 90도 회전 (beforeAngle: ${beforeAngle}도)`);
    }

    // 케이스 2-1: nextAngle이 90도에 가까움 → 태양광 패널 궤도
    if (this.angleChecker.is90DegreeTurn(nextAngle)) {
      if (isTargetFile) {
        console.log(`  ✅ 케이스 2-1: 태양광 패널 궤도 (nextAngle: ${nextAngle}도)`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: false
      });
    }

    // 케이스 2-2: nextAngle이 180도에 가까움 → 180도 회전 후 직선 경로
    if (this.angleChecker.is180DegreeTurn(nextAngle)) {
      if (isTargetFile) {
        console.log(`  ✅ 케이스 2-2: 180도 회전 후 직선 경로 (nextAngle: ${nextAngle}도)`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: false
      });
    }

    // 케이스 2-3: nextAngle이 작음 → 90도 회전 후 직선 경로
    if (absNext <= small) {
      if (isTargetFile) {
        console.log(`  ✅ 케이스 2-3: 90도 회전 후 직선 경로 (nextAngle: ${nextAngle}도)`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: false
      });
    }

    // 케이스 2-4: 그 외 각도 → 방향 전환
    if (isTargetFile) {
      console.log(`  ❌ 케이스 2-4: 방향 전환으로 판단 (nextAngle: ${nextAngle}도)`);
    }
    return this.createAngleInfoResult(prevPoint, nextPoint, {
      isFirstVector: false,
      isSecondVector: false,
      beforeAngle,
      nextAngle,
      isDirectionChange: true
    });
  }

  /**
   * 180도 회전 케이스 판단 (케이스 3)
   */
  check180DegreeTurnCases(beforeAngle, nextAngle, prevPoint, nextPoint, isTargetFile) {
    const absNext = Math.abs(nextAngle);
    const small = this.config.angles.straight.small;

    if (isTargetFile) {
      console.log(`  🔍 케이스 3: 180도 회전 (beforeAngle: ${beforeAngle}도)`);
    }

    // 케이스 3-1: nextAngle이 작음 → 180도 회전 후 직선 경로
    if (absNext <= small) {
      if (isTargetFile) {
        console.log(`  ✅ 케이스 3-1: 180도 회전 후 직선 경로 (nextAngle: ${nextAngle}도)`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: false
      });
    }

    // 케이스 3-2: 그 외는 방향 전환
    if (isTargetFile) {
      console.log(`  ❌ 케이스 3-2: 방향 전환으로 판단 (nextAngle: ${nextAngle}도)`);
    }
    return this.createAngleInfoResult(prevPoint, nextPoint, {
      isFirstVector: false,
      isSecondVector: false,
      beforeAngle,
      nextAngle,
      isDirectionChange: true
    });
  }

  /**
   * 벡터 각도 분석 및 needFilm 체크 대상 판단
   * @param {Object} beforePrevPoint - 이전 이전 포인트
   * @param {Object} prevPoint - 이전 포인트
   * @param {Object} nextPoint - 다음 포인트
   * @returns {Object|null} {prevPoint, nextPoint, angleInfo} 또는 null
   */
  filterCorrectVector(beforePrevPoint, prevPoint, nextPoint) {
    const beforePrevVector = beforePrevPoint?.vector;
    const prevVector = prevPoint?.vector;
    const nextVector = nextPoint?.vector;
    
    // 디버깅 파일 확인
    const prevFileName = prevPoint?.name || '';
    const nextFileName = nextPoint?.name || '';
    const isTargetFile = this.isDebugFile(prevFileName) || this.isDebugFile(nextFileName);
    
    if (isTargetFile) {
      console.log(`🔍 [filterCorrectVector] ${prevFileName} → ${nextFileName}`);
      console.log(`  - beforePrevPoint:`, beforePrevPoint?.name, 'vector:', beforePrevVector);
      console.log(`  - prevPoint:`, prevPoint?.name, 'vector:', prevVector);
      console.log(`  - nextPoint:`, nextPoint?.name, 'vector:', nextVector);
    }
    
    console.log('테스트imageLocation vector:', beforePrevPoint, prevVector, nextVector);
    
    const vectorAngle = (v1, v2) => calculateVectorAngle(v1, v2);
    
    // 첫번째 벡터 처리
    if (!beforePrevPoint && !prevVector) {
      return this.handleFirstVector(prevPoint, nextPoint, isTargetFile);
    }

    // 두번째 벡터 처리
    if (beforePrevPoint && !beforePrevVector && prevVector && nextVector) {
      const nextAngle = vectorAngle(prevVector, nextVector);
      return this.handleSecondVector(prevPoint, nextPoint, nextAngle, isTargetFile);
    }
    
    // 세번째 벡터부터: 첫번째+2번째 벡터 각도(beforeAngle)와 2번째+3번째 벡터 각도(nextAngle) 조합으로 판단
    if (beforePrevVector && prevVector && nextVector) {
      const beforeAngle = vectorAngle(beforePrevVector, prevVector);
      const nextAngle = vectorAngle(prevVector, nextVector);
      const absBeforeAngle = Math.abs(beforeAngle);
      const absNextAngle = Math.abs(nextAngle);

      if (isTargetFile) {
        console.log(`  🔍 세번째 벡터 조건 체크`);
        console.log(`  - beforeAngle:`, beforeAngle, 'abs:', absBeforeAngle);
        console.log(`  - nextAngle:`, nextAngle, 'abs:', absNextAngle);
      }
      
      console.log('테스트imageLocation angle - beforeAngle:', beforeAngle, 'nextAngle:', nextAngle);

      if (isNaN(beforeAngle) || isNaN(nextAngle)) {
        if (isTargetFile) {
          console.log(`  ❌ 각도 계산 실패 (NaN)`);
        }
        return null;
      }

      // 케이스 1: 직선 경로 판단
      const straightPathResult = this.checkStraightPathCases(beforeAngle, nextAngle, prevPoint, nextPoint, isTargetFile);
      if (straightPathResult) {
        return straightPathResult;
      }

      // 케이스 2: 90도 회전 판단
      if (this.angleChecker.is90DegreeTurn(beforeAngle)) {
        return this.check90DegreeTurnCases(beforeAngle, nextAngle, prevPoint, nextPoint, isTargetFile);
      }

      // 케이스 3: 180도 회전 판단
      if (this.angleChecker.is180DegreeTurn(beforeAngle)) {
        return this.check180DegreeTurnCases(beforeAngle, nextAngle, prevPoint, nextPoint, isTargetFile);
      }

      // 케이스 4: 방향 전환 후 직선 경로 판단
      if (this.angleChecker.isDirectionChangeToStraight(beforeAngle, nextAngle)) {
        if (isTargetFile) {
          console.log(`  ✅ 케이스 4: 방향 전환 후 직선 경로 (beforeAngle: ${beforeAngle}도, nextAngle: ${nextAngle}도)`);
        }
        return this.createAngleInfoResult(prevPoint, nextPoint, {
          isFirstVector: false,
          isSecondVector: false,
          beforeAngle,
          nextAngle,
          isDirectionChange: false
        });
      }

      // 케이스 5: 태양광 패널 구간 특별 처리
      // beforeAngle이나 nextAngle이 90도보다 크지만 120도 미만인 경우
      const large = this.config.angles.straight.large;
      if ((absBeforeAngle > large && absBeforeAngle < 120) || (absNextAngle > large && absNextAngle < 120)) {
        if (isTargetFile) {
          console.log(`  ✅ 케이스 5: 태양광 패널 구간 (beforeAngle: ${beforeAngle}도, nextAngle: ${nextAngle}도)`);
        }
        return this.createAngleInfoResult(prevPoint, nextPoint, {
          isFirstVector: false,
          isSecondVector: false,
          beforeAngle,
          nextAngle,
          isDirectionChange: false
        });
      }

      // 케이스 6: 그 외 각도 조합 → 방향 전환
      if (isTargetFile) {
        console.log(`  ❌ 케이스 6: 그 외 각도 조합 - 방향 전환으로 판단`);
        console.log(`  - beforeAngle: ${beforeAngle}도 (abs: ${absBeforeAngle}), nextAngle: ${nextAngle}도 (abs: ${absNextAngle})`);
      }
      return this.createAngleInfoResult(prevPoint, nextPoint, {
        isFirstVector: false,
        isSecondVector: false,
        beforeAngle,
        nextAngle,
        isDirectionChange: true
      });
    }
    
    if (isTargetFile) {
      console.log(`  ❌ 세번째 벡터 조건 불만족 - beforePrevVector:`, !!beforePrevVector, 'prevVector:', !!prevVector, 'nextVector:', !!nextVector);
    }
    return null;
  }
}